import { globalHistory } from "@reach/router";
import React, { useEffect, useState, useRef } from "react";

import makeZapierRequest from "../../zapier/zapier";
import * as s from "./InsuranceFormPopup.module.css";
import { US_STATES } from "../../constants/states";
import { REFERRAL_SOURCE_OPTIONS } from "../../constants/referralSource";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import {
  DAZOS_ASSIGNED,
  DAZOS_PUBLIC_ID_INSURANCE_FORM,
  DAZOS_URL,
} from "../../constants/dazos";
import Toast from "../toast/Toast";

async function sendToDazos(data) {
  const payload = {
    firstname: data.firstName,
    lastname: data.lastName,
    cf_864: data.patientDOB,
    phone: data.phoneNumber,
    state: data.state,
    email: data.email,
    cf_3035: data.insuranceCompany || "",
    cf_2428: data.memberId || "",
    description: data.otherInformation || "",
    referralSource: data.referralSource || "",
    assigned_user_id: DAZOS_ASSIGNED,
    publicid: DAZOS_PUBLIC_ID_INSURANCE_FORM,
    leadsource: "Website Popup",
    cf_1445: "181",
  };

  try {
    const res = await fetch(DAZOS_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    // Fallback to FORM-encoded if JSON isn’t accepted
    if (!res.ok) {
      const form = new URLSearchParams();
      Object.entries(payload).forEach(([k, v]) => form.append(k, v));
      await fetch(DAZOS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: form.toString(),
      });
    }
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.error("Dazos webhook failed:", err);
    }
  }
}

const InsuranceFormPopup = () => {
  const [showInsurancePopup, setShowInsurancePopup] = useState(false);

  // Re-initialize Turnstile when shown
  useEffect(() => {
    if (window.turnstile && showInsurancePopup) {
      window.turnstile?.render("#insurance-popup-turnstile");
    }
  }, [showInsurancePopup]);

  // Open popup via hash
  useEffect(() => {
    return globalHistory.listen((data) => {
      if ("#insurance" == data?.location?.hash) {
        setShowInsurancePopup(true);
      }
    });
  }, [showInsurancePopup]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    patientDOB: "",
    phoneNumber: "",
    email: "",
    insuranceCompany: "",
    memberId: "",
    otherInformation: "",
    acceptance: false,
    state: "",
    referralSource: "",
    referalSourceCustom: "",
    website: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mailSent, setMailSent] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const nameRegex = /^[a-zA-Z.'\- ]{1,25}$/;
  const usPhoneRegex = /^(\+1[-\s.]?)?(\(?\d{3}\)?[-\s.]?)?\d{3}[-\s.]?\d{4}$/;

  const getAge = (mmddyyyy) => {
    const dob = new Date(mmddyyyy);
    if (isNaN(dob.getTime())) return null;

    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age;
  };

  const validateField = (name, value) => {
    const v = typeof value === "string" ? value.trim() : value;

    switch (name) {
      case "firstName":
        if (!v) return "First name is required.";
        if (!nameRegex.test(v))
          return "Only letters, spaces, dots, hyphens, and apostrophes allowed. Max 25 characters.";
        return "";

      case "lastName":
        if (!v) return "Last name is required.";
        if (!nameRegex.test(v))
          return "Only letters, spaces, dots, hyphens, and apostrophes allowed. Max 25 characters.";
        return "";

      case "patientDOB":
        if (!v) return "Date of birth is required.";
        {
          const age = getAge(v);
          if (age === null || age < 18)
            return "You must be at least 18 years old.";
        }
        return "";

      case "state":
        if (!v) return "State is required.";
        return "";

      case "phoneNumber":
        if (!v) return "Phone number is required.";
        if (!usPhoneRegex.test(v))
          return "Please enter a valid US phone number.";
        return "";

      case "email":
        if (!v) return "Email is required.";
        if (!emailRegex.test(v)) return "Please enter a valid email address.";
        if (v.length > 50) return "Email address cannot exceed 50 characters.";
        return "";

      case "insuranceCompany":
        if (v && v.length > 50)
          return "Insurance provider name can be up to 50 characters.";
        return "";

      case "memberId":
        if (v && v.length > 50) return "Member ID can be up to 50 characters.";
        return "";

      case "otherInformation":
        if (v && v.length > 3000)
          return "Message cannot exceed 3000 characters.";
        return "";

      case "acceptance":
        if (!v) return "You must accept the privacy policy.";
        return "";

      case "referalSourceCustom":
        if (v && v.length > 100) return "Cannot exceed 100 characters.";
        return "";

      default:
        return "";
    }
  };

  const setFieldValue = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Live validate if already touched
    setErrors((prevErrs) => {
      if (!touched[name]) return prevErrs;

      const msg = validateField(name, value);
      const next = { ...prevErrs };
      if (msg) next[name] = msg;
      else delete next[name];
      return next;
    });
  };

  const markTouchedAndValidate = (name, value = formData[name]) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const msg = validateField(name, value);

    setErrors((prev) => {
      const next = { ...prev };
      if (msg) next[name] = msg;
      else delete next[name];
      return next;
    });
  };

  const validateAll = () => {
    const fields = [
      "firstName",
      "lastName",
      "patientDOB",
      "phoneNumber",
      "email",
      "state",
      "insuranceCompany",
      "memberId",
      "otherInformation",
      "acceptance",
    ];

    const newErrors = {};
    for (const f of fields) {
      const msg = validateField(f, formData[f]);
      if (msg) newErrors[f] = msg;
    }
    setErrors(newErrors);

    const newTouched = {};
    fields.forEach((f) => (newTouched[f] = true));
    setTouched(newTouched);

    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;
    setFieldValue(name, nextValue);
  };

  const handleBlur = (e) => {
    const { name, value, type, checked } = e.target;
    const v = type === "checkbox" ? checked : value;
    markTouchedAndValidate(name, v);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Honeypot
    if ((formData.website || "").trim().length > 0) return;

    if (validateAll()) {
      setIsSubmitting(true);
      setErrorMessage("");

      const normalizedReferralSource =
        formData.referralSource === "Other"
          ? formData.referalSourceCustom.trim()
            ? `Other - ${formData.referalSourceCustom.trim()}`
            : "Other"
          : formData.referralSource;

      const { website, referalSourceCustom, ...safeFormData } = formData;
      const normalizedFormData = {
        ...safeFormData,
        referralSource: normalizedReferralSource,
      };

      const tokenEl = document.querySelector(
        '#insurance-popup-turnstile input[name="cf-turnstile-response"]'
      );
      const token = tokenEl?.value || "";

      const templateParams = {
        firstName: normalizedFormData.firstName,
        lastName: normalizedFormData.lastName,
        patientDOB: normalizedFormData.patientDOB,
        phoneNumber: normalizedFormData.phoneNumber,
        email: normalizedFormData.email,
        insuranceCompany: normalizedFormData.insuranceCompany,
        memberId: normalizedFormData.memberId,
        otherInformation: normalizedFormData.otherInformation,
        acceptance: normalizedFormData.acceptance,
        state: normalizedFormData.state,
        referralSource: normalizedFormData.referralSource,
        turnstileToken: token,
      };

      fetch("https://send.thehouseoflife.com/api/email-verify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.GATSBY_API_EMAIL_KEY,
        },
        body: JSON.stringify(templateParams),
      })
        .then((res) => {
          if (!res.ok) throw new Error("Request failed");

          setMailSent(true);

          const payload = {
            event: "formSubmit",
            type: "insurance",
            ...normalizedFormData,
          };
          makeZapierRequest(payload);
          sendToDazos(normalizedFormData);

          setShowInsurancePopup(false);

          setFormData({
            firstName: "",
            lastName: "",
            patientDOB: "",
            phoneNumber: "",
            email: "",
            insuranceCompany: "",
            memberId: "",
            otherInformation: "",
            acceptance: false,
            state: "",
            referralSource: "",
            referalSourceCustom: "",
            website: "",
          });
          setErrors({});
          setTouched({});
        })
        .catch((err) => {
          console.log(err);
          setErrorMessage("One or more fields are invalid.");
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      setErrorMessage("One or more fields are invalid.");
    }
  };

  // State dropdown
  const [stateDropdownVisible, setStateDropdownVisible] = useState(false);
  // How did you hear about us dropdown
  const [referralSourceDropdownVisible, setReferralSourceDropdownVisible] =
    useState(false);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(`.${s.customDropdownWrapper}`)) {
        setStateDropdownVisible(false);
        setReferralSourceDropdownVisible(false);
      }
    };
    // Use capture phase because modal content stops click bubbling.
    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  // Calendar
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDateChange = (date) => {
    if (date) {
      const formattedDate = `${("0" + (date.getMonth() + 1)).slice(-2)}/${(
        "0" + date.getDate()
      ).slice(-2)}/${date.getFullYear()}`;

      setFieldValue("patientDOB", formattedDate);
      markTouchedAndValidate("patientDOB", formattedDate);
    }
    setShowCalendar(false);
  };

  const handleInputClick = () => {
    setShowCalendar((prev) => !prev);
  };

  const today = new Date();
  const minDate = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );

  const hasAnyError = Object.keys(errors).length > 0;
  const hasAnyTouched = Object.keys(touched).length > 0;

  useEffect(() => {
    if (hasAnyError && hasAnyTouched) {
      setErrorMessage("One or more fields are invalid.");
    } else {
      setErrorMessage("");
    }
  }, [hasAnyError, hasAnyTouched]);

  return (
    <>
      {mailSent && (
        <Toast
          message="Your request was submitted successfully!"
          onClose={() => setMailSent(false)}
        />
      )}

      {showInsurancePopup ? (
        <div
          className={`${s.block}`}
          onClick={() => setShowInsurancePopup(false)}
        >
          <div className={`container `}>
            <div
              className={` ${s.wrapper}`}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className={s.close}
                onClick={() => setShowInsurancePopup(false)}
              >
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 26 26"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line
                    x1="19.0732"
                    y1="19.1104"
                    x2="6.34531"
                    y2="6.38247"
                    stroke="#2C3A33"
                  />
                  <line
                    x1="6.34525"
                    y1="19.0732"
                    x2="19.0732"
                    y2="6.34528"
                    stroke="#2C3A33"
                  />
                </svg>
              </button>

              <form className={`${s.form}`} onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-5 col-xxl-4">
                    <h2 className={s.title}>
                      Does Your Insurance Cover Addiction Treatment?
                    </h2>
                    <div className={s.description}>
                      Treatments at House of Life are Covered by Most Major
                      Insurance Plans. Check yours below.
                    </div>
                  </div>

                  <div className="col-lg-7 offset-xxl-1">
                    <div className="row" style={{ rowGap: "30px" }}>
                      {/* Honeypot */}
                      <div
                        aria-hidden="true"
                        style={{
                          position: "absolute",
                          left: "-10000px",
                          top: "auto",
                          width: "1px",
                          height: "1px",
                          overflow: "hidden",
                        }}
                      >
                        <label htmlFor="website">Website</label>
                        <input
                          id="website"
                          type="text"
                          name="website"
                          value={formData.website}
                          onChange={handleChange}
                          tabIndex={-1}
                          autoComplete="new-password"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className={s.labelField}>
                          First Name <span>*</span>
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          maxLength={25}
                          value={formData.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="First Name"
                          className={s.inputField}
                        />
                        {touched.firstName && errors.firstName && (
                          <p className={s.errorText}>{errors.firstName}</p>
                        )}
                      </div>

                      <div className="col-md-6">
                        <label className={s.labelField}>
                          Last Name <span>*</span>
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          maxLength={25}
                          value={formData.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Last Name"
                          className={s.inputField}
                        />
                        {touched.lastName && errors.lastName && (
                          <p className={s.errorText}>{errors.lastName}</p>
                        )}
                      </div>

                      <div className="col-md-6">
                        <label className={s.labelField}>
                          Date of Birth <span>*</span>
                        </label>
                        <div className={s.datepicker_container}>
                          <input
                            ref={inputRef}
                            type="text"
                            name="patientDOB"
                            value={formData.patientDOB}
                            placeholder="MM/DD/YYYY"
                            onClick={handleInputClick}
                            className={s.inputField}
                            readOnly
                          />
                          {showCalendar && (
                            <div
                              ref={calendarRef}
                              className={s.calendar_container}
                            >
                              <DayPicker
                                selected={
                                  formData.patientDOB
                                    ? new Date(formData.patientDOB)
                                    : null
                                }
                                onDayClick={handleDateChange}
                                dateFormat="MM/dd/yyyy"
                                mode="single"
                                required
                                captionLayout={"dropdown"}
                                endMonth={minDate}
                                style={{
                                  "--rdp-accent-color": "#6BA3C4",
                                }}
                              />
                            </div>
                          )}
                        </div>

                        {touched.patientDOB && errors.patientDOB && (
                          <p className={s.errorText}>{errors.patientDOB}</p>
                        )}
                      </div>

                      <div className="col-md-6">
                        <label className={s.labelField}>
                          Phone <span>*</span>
                        </label>
                        <input
                          type="text"
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="+123456789"
                          className={s.inputField}
                        />
                        {touched.phoneNumber && errors.phoneNumber && (
                          <p className={s.errorText}>{errors.phoneNumber}</p>
                        )}
                      </div>

                      <div className="col-md-12">
                        <label className={s.labelField}>
                          State <span>*</span>
                        </label>
                        <div className={s.customDropdownWrapper}>
                          <div
                            className={`${s.inputField} ${s.dropdownTrigger}`}
                            onClick={() => {
                              setStateDropdownVisible((prev) => !prev);
                              setReferralSourceDropdownVisible(false);
                            }}
                            onBlur={() =>
                              markTouchedAndValidate("state", formData.state)
                            }
                            tabIndex={0}
                            role="button"
                            aria-haspopup="listbox"
                            aria-expanded={stateDropdownVisible}
                          >
                            {formData.state || "Select a state"}
                          </div>
                          {stateDropdownVisible && (
                            <ul className={s.dropdownList} role="listbox">
                              {US_STATES.map((state) => (
                                <li
                                  key={state}
                                  className={s.dropdownItem}
                                  role="option"
                                  aria-selected={formData.state === state}
                                  onClick={() => {
                                    setFieldValue("state", state);
                                    markTouchedAndValidate("state", state);
                                    setStateDropdownVisible(false);
                                  }}
                                >
                                  {state}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        {touched.state && errors.state && (
                          <p className={s.errorText}>{errors.state}</p>
                        )}
                      </div>

                      <div className="col-md-12">
                        <label className={s.labelField}>
                          Email Address <span>*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          maxLength={50}
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="your@email.com"
                          className={s.inputField}
                        />
                        {touched.email && errors.email && (
                          <p className={s.errorText}>{errors.email}</p>
                        )}
                      </div>

                      <div>
                        <label className={s.labelField}>
                          How did you hear about us?
                        </label>
                        <div className={"row"}>
                          <div
                            className={`${s.customDropdownWrapper} ${
                              formData.referralSource === "Other"
                                ? "col-md-6"
                                : "col-md-12"
                            }`}
                          >
                            <div
                              className={`${s.inputField} ${s.dropdownTrigger}`}
                              onClick={() => {
                                setReferralSourceDropdownVisible(
                                  (prev) => !prev
                                );
                                setStateDropdownVisible(false);
                              }}
                              onBlur={() =>
                                markTouchedAndValidate(
                                  "referralSource",
                                  formData.referralSource
                                )
                              }
                              tabIndex={0}
                              role="button"
                              aria-haspopup="listbox"
                              aria-expanded={referralSourceDropdownVisible}
                            >
                              {formData.referralSource || "Select a source"}
                            </div>
                            {referralSourceDropdownVisible && (
                              <ul
                                className={s.dropdownList}
                                role="listbox"
                                style={{
                                  width:
                                    formData.referralSource === "Other"
                                      ? "calc(100% - 24px)"
                                      : "calc(100% - 24px)",
                                }}
                              >
                                {REFERRAL_SOURCE_OPTIONS.map((option) => (
                                  <li
                                    key={option}
                                    className={s.dropdownItem}
                                    role="option"
                                    aria-selected={
                                      formData.referralSource === option
                                    }
                                    onClick={() => {
                                      setFieldValue("referralSource", option);
                                      if (option !== "Other") {
                                        setFieldValue(
                                          "referalSourceCustom",
                                          ""
                                        );
                                      }
                                      setReferralSourceDropdownVisible(false);
                                    }}
                                  >
                                    {option}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          {formData.referralSource === "Other" && (
                            <div className={`col-6 ${s.referalSourceCustom}`}>
                              <input
                                type="text"
                                name="referalSourceCustom"
                                maxLength={100}
                                value={formData.referalSourceCustom}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Please specify"
                                className={s.inputField}
                              />
                              {touched.referalSourceCustom &&
                                errors.referalSourceCustom && (
                                  <p className={s.errorText}>
                                    {errors.referalSourceCustom}
                                  </p>
                                )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label className={s.labelField}>
                          Insurance Provider
                        </label>
                        <input
                          type="text"
                          name="insuranceCompany"
                          maxLength={50}
                          value={formData.insuranceCompany}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Insurance Provider"
                          className={s.inputField}
                        />
                        {touched.insuranceCompany &&
                          errors.insuranceCompany && (
                            <p className={s.errorText}>
                              {errors.insuranceCompany}
                            </p>
                          )}
                      </div>

                      <div className="col-md-6">
                        <label className={s.labelField}>Member ID</label>
                        <input
                          type="text"
                          name="memberId"
                          maxLength={50}
                          value={formData.memberId}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Member ID"
                          className={s.inputField}
                        />
                        {touched.memberId && errors.memberId && (
                          <p className={s.errorText}>{errors.memberId}</p>
                        )}
                      </div>

                      <div className="col-md-12">
                        <label className={s.labelField}>Message</label>
                        <textarea
                          name="otherInformation"
                          maxLength={3000}
                          value={formData.otherInformation}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Tell us more..."
                          className={s.textAreaField}
                        />
                        {touched.otherInformation &&
                          errors.otherInformation && (
                            <p className={s.errorText}>
                              {errors.otherInformation}
                            </p>
                          )}
                      </div>

                      <div className="col-md-12">
                        <label className={s.checboxLabel}>
                          <input
                            type="checkbox"
                            name="acceptance"
                            className="d-none"
                            checked={formData.acceptance}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <span className="me-3">
                            {formData.acceptance ? (
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M8.52947 15.0231C8.81365 15.0231 9.05942 14.8925 9.24375 14.6083L14.405 6.52076C14.5203 6.35179 14.6201 6.14441 14.6201 5.96008C14.6201 5.55302 14.2591 5.29956 13.8828 5.29956C13.66 5.29956 13.4373 5.43013 13.2683 5.69127L8.49106 13.2873L5.94882 10.0999C5.74913 9.84641 5.54176 9.75425 5.29598 9.75425C4.91964 9.75425 4.60474 10.0538 4.60474 10.4609C4.60474 10.6529 4.68154 10.8602 4.81211 11.0215L7.77678 14.616C8.01487 14.9079 8.24529 15.0231 8.52947 15.0231Z"
                                  fill="#6BA3C8"
                                />
                                <rect
                                  x="0.5"
                                  y="0.5"
                                  width="19"
                                  height="19"
                                  rx="2.5"
                                  stroke="#6BA3C8"
                                />
                              </svg>
                            ) : (
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="0.5"
                                  y="0.5"
                                  width="19"
                                  height="19"
                                  rx="2.5"
                                  stroke="#6BA3C8"
                                />
                              </svg>
                            )}
                          </span>
                          <div>
                            I Have Read And Accept The{" "}
                            <a
                              href="/privacy-policy"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Privacy Policy
                            </a>{" "}
                            <span className={s.required}>*</span>
                          </div>
                        </label>

                        {touched.acceptance && errors.acceptance && (
                          <p className={s.errorText}>{errors.acceptance}</p>
                        )}
                      </div>

                      <div
                        className="cf-turnstile"
                        id="insurance-popup-turnstile"
                        data-sitekey={process.env.GATSBY_TURNSTILE_SITE_KEY}
                      />

                      <div className="col-md-12">
                        <button
                          type="submit"
                          className={s.submitButton}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Sending..." : "Submit"}
                        </button>
                        {!!errorMessage && (
                          <p className={s.errorText}>
                            <big>{errorMessage}</big>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

export default InsuranceFormPopup;
